import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { Documento } from '@/lib/index';

interface UseDocumentiReturn {
  documenti: Documento[];
  loading: boolean;
  error: string | null;
  uploadDocumento: (
    file: File,
    condominioId: string,
    tipo: 'consuntivo' | 'preventivo',
    anno: number
  ) => Promise<{ error: Error | null; documento?: Documento }>;
  deleteDocumento: (id: string) => Promise<void>;
  reanalizzaDocumento: (id: string) => Promise<void>;
  refetch: () => Promise<void>;
}

export function useDocumenti(condominioId?: string): UseDocumentiReturn {
  const [documenti, setDocumenti] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const { user } = useAuth();

  const fetchDocumenti = useCallback(async () => {
    if (!mountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      if (!user) {
        if (mountedRef.current) {
          setDocumenti([]);
          setLoading(false);
        }
        return;
      }

      let query = supabase
        .from('documenti')
        .select(`
          *,
          condomini!inner(nome)
        `)
        .order('created_at', { ascending: false });

      if (condominioId) {
        query = query.eq('condominio_id', condominioId);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        throw new Error(fetchError.message);
      }

      if (mountedRef.current) {
        const mappedData = (data || []).map((doc: any) => ({
          ...doc,
          condominio_nome: doc.condomini?.nome || '',
        }));
        setDocumenti(mappedData as Documento[]);
      }
    } catch (err) {
      if (mountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Errore caricamento documenti';
        setError(msg);
        setDocumenti([]);
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [condominioId, user]);

  const uploadDocumento = async (
    file: File,
    condominioId: string,
    tipo: 'consuntivo' | 'preventivo',
    anno: number
  ) => {
    try {
      if (!user) {
        throw new Error('Utente non autenticato');
      }

      const timestamp = Date.now();
      // Percorso compatibile con struttura reale storage
      const suffix = Math.random().toString(36).slice(2, 8);
      const fileName = `${tipo}_${anno}_${timestamp}_${suffix}.pdf`;
      // La colonna nel DB si chiama storage_path
      const storagePath = `${user.id}/${condominioId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('documenti-pdf')
        .upload(storagePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (uploadError) {
        throw new Error(`Errore upload: ${uploadError.message}`);
      }

      const { data: documento, error: insertError } = await supabase
        .from('documenti')
        .insert({
          condominio_id: condominioId,
          user_id: user.id,          // ← obbligatorio: NOT NULL constraint
          tipo,
          anno,
          storage_path: storagePath,
          file_path: storagePath,
          nome_file: file.name,
          file_size: file.size,
          stato: 'caricato',
        })
        .select()
        .single();

      if (insertError) {
        await supabase.storage.from('documenti-pdf').remove([storagePath]);
        throw new Error(`Errore creazione record: ${insertError.message}`);
      }

      if (mountedRef.current) {
        await fetchDocumenti();
      }

      return { error: null as Error | null, documento: documento as Documento };
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore upload documento';
      return { error: new Error(msg) };
    }
  };

  const deleteDocumento = async (id: string): Promise<void> => {
    try {
      const documento = documenti.find((d) => d.id === id);

      const { error: deleteError } = await supabase
        .from('documenti')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw new Error(`Errore eliminazione: ${deleteError.message}`);
      }

      const pathToRemove = documento?.storage_path || documento?.file_path;
      if (pathToRemove) {
        await supabase.storage.from('documenti-pdf').remove([pathToRemove]);
      }

      if (mountedRef.current) {
        setDocumenti((prev) => prev.filter((d) => d.id !== id));
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore eliminazione documento';
      throw new Error(msg);
    }
  };

  const reanalizzaDocumento = async (id: string): Promise<void> => {
    try {
      const { error: updateError } = await supabase
        .from('documenti')
        .update({ stato: 'caricato' })
        .eq('id', id);

      if (updateError) {
        throw new Error(`Errore reset stato: ${updateError.message}`);
      }

      if (mountedRef.current) {
        await fetchDocumenti();
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore rianalisi documento';
      throw new Error(msg);
    }
  };

  const refetch = useCallback(async () => {
    await fetchDocumenti();
  }, [fetchDocumenti]);

  useEffect(() => {
    mountedRef.current = true;
    fetchDocumenti();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchDocumenti]);

  return {
    documenti,
    loading,
    error,
    uploadDocumento,
    deleteDocumento,
    reanalizzaDocumento,
    refetch,
  };
}
